import {
  AllowMeErrorCauseReasons,
  AllowMeErrorOutcomes,
  AllowMeErrorPhases,
  AllowMeErrorReasons,
  AllowMeLikelyCauseLabels,
  AllowMeProcessSteps,
  buildAllowMeCorrectionHint,
  inferAllowMeLikelyCause,
} from 'services/error_logging/allowme-error-reasons';
import {
  allowMeInitExceptionEntry,
  allowMeInitFailedEntry,
  allowMeInitMissingKeyEntry,
  buildAllowMeErrorEntry,
} from 'services/error_logging/allowme-error-log-entry';
import { logAllowMeError } from 'services/error_logging/allowme-error-log-service';

const SDK_URL_HLG = 'https://stage-sdk-js.allowme.com.br/v2/allowme.min.js';
const SDK_URL_PRD = 'https://sdk-js.allowme.com.br/v2/allowme.min.js';
const COLLECT_TIMEOUT_MS = 5000;
const INIT_TIMEOUT_MS = 8000;
const COLLECT_EVENT_PREFIX = 'login';
const SCRIPT_ID = 'allowme-sdk-script';
const LOG_PREFIX = '[AllowMe]';

// TODO: remover após validar key/URL corretas em homolog
const TEMP_DEBUG_LOG_KEY_AND_URL = true;

let initSucceeded = false;
let sdkLoadPromise = null;
let initPromise = null;
let lastEnabled = true;
let lastInitDiagnostic = null;
let initPluginWasInvoked = false;
let lastCollectFailure = null;
let lastInitDurationMs = null;
let lastInitResultType = null;
let collectAttempt = 0;
let lastCollectedFingerprint = null;
let lastSdkNetwork = [];

const resolveAmbiente = () =>
  Number(process.env.REACT_APP_API_ENUM) === 0 ? 'prd' : 'hlg';

const maskApiKey = (key) => {
  const trimmed = (key || '').trim();
  if (!trimmed) return '(vazia)';
  if (trimmed.length <= 8) return '****';
  return `${trimmed.slice(0, 4)}...${trimmed.slice(-4)}`;
};

const maskFingerprint = (fingerprint) => {
  if (!fingerprint) return null;
  const value = String(fingerprint).trim();
  if (!value) return null;
  return value.length > 12
    ? `${value.slice(0, 8)}...${value.slice(-4)} (${value.length} chars)`
    : '(curto)';
};

const logStep = (step, message, extra) => {
  if (extra && Object.keys(extra).length) {
    console.info(`${LOG_PREFIX} [${step}] ${message}`, extra);
    return;
  }
  console.info(`${LOG_PREFIX} [${step}] ${message}`);
};

const logWarn = (step, message, extra) => {
  if (extra && Object.keys(extra).length) {
    console.warn(`${LOG_PREFIX} [${step}] ${message}`, extra);
    return;
  }
  console.warn(`${LOG_PREFIX} [${step}] ${message}`);
};

const logError = (step, message, error, extra = {}) => {
  console.error(`${LOG_PREFIX} [${step}] ${message}`, {
    error: (error && error.message) || String(error),
    ...extra,
  });
};

const logKeyDebug = (apiKey, sdkUrl, ambiente) => {
  if (!TEMP_DEBUG_LOG_KEY_AND_URL) return;

  const key = (apiKey || '').trim();
  console.info(`${LOG_PREFIX} [debug-temp] Key e URL em uso`, {
    ambiente,
    apiKeyPrefix: key ? key.slice(0, 12) : '(vazia)',
    apiKeyLength: key.length,
    sdkUrl,
  });
};

export const isAllowMeEnabled = () =>
  process.env.REACT_APP_ALLOWME_ENABLED !== 'false';

const resolveSdkUrl = () =>
  Number(process.env.REACT_APP_API_ENUM) === 0 ? SDK_URL_PRD : SDK_URL_HLG;

const isValidFingerprint = (fingerprint) => {
  if (!fingerprint || typeof fingerprint !== 'string') return false;
  const trimmed = fingerprint.trim();
  if (!trimmed) return false;
  return !isNativeFailureMessage(trimmed);
};

const isNativeFailureMessage = (value) => {
  if (value == null) return false;
  const text = String(value).trim().toLowerCase();
  if (!text) return false;
  return text.startsWith('error:') || text.indexOf('error trying to setup') !== -1;
};

const withTimeout = (promise, timeoutMs, operation) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const error = new Error(
        `AllowMe timeout after ${timeoutMs}ms during ${operation}`
      );
      error.code = 'ALLOWME_TIMEOUT';
      reject(error);
    }, timeoutMs);

    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const stringifyLogArg = (value) => {
  if (value instanceof Error) return value.message || String(value);
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const listAlwmMethods = () => {
  if (typeof window === 'undefined' || !window.Alwm) return [];
  try {
    return Object.keys(window.Alwm).filter(
      (key) => typeof window.Alwm[key] === 'function'
    );
  } catch (error) {
    return [];
  }
};

const EXPECTED_ALLOWME_ENDPOINTS = ['cookie', 'key_agreement'];
const SENSITIVE_HEADER_NAME = /authorization|cookie|set-cookie|api-?key|token|secret|x-api/i;

const classifyAllowMeEndpoint = (url) => {
  const href = String(url || '').toLowerCase();
  if (href.indexOf('/v1/web/cookie') !== -1) return 'cookie';
  if (href.indexOf('/v1/crypto/key_agreement') !== -1) return 'key_agreement';
  if (/allowme|serasa/i.test(href)) return 'other';
  return null;
};

const sanitizeObservedUrl = (url) => {
  const href = String(url || '');
  const queryIndex = href.indexOf('?');
  return (queryIndex === -1 ? href : href.slice(0, queryIndex)).slice(0, 180);
};

const sanitizeHeaders = (headers) => {
  if (!headers) return undefined;
  const out = {};
  Object.keys(headers).forEach((name) => {
    const value = headers[name] == null ? '' : String(headers[name]);
    out[name] = SENSITIVE_HEADER_NAME.test(name)
      ? maskApiKey(value)
      : value.slice(0, 200);
  });
  return Object.keys(out).length ? out : undefined;
};

const readFetchHeaders = (input, init) => {
  const out = {};
  const apply = (headers) => {
    if (!headers) return;
    if (typeof headers.forEach === 'function') {
      headers.forEach((value, name) => {
        out[name] = value;
      });
      return;
    }
    if (Array.isArray(headers)) {
      headers.forEach((pair) => {
        if (pair && pair.length >= 2) out[pair[0]] = pair[1];
      });
      return;
    }
    if (typeof headers === 'object') {
      Object.keys(headers).forEach((name) => {
        out[name] = headers[name];
      });
    }
  };
  apply(init && init.headers);
  apply(input && input.headers);
  return out;
};

const rememberSdkNetwork = (entry) => {
  lastSdkNetwork = lastSdkNetwork.concat([entry]).slice(-12);
};

const mergeSdkNetwork = (snapshot) => {
  const combined = [];
  const seen = {};
  const append = (item) => {
    if (!item) return;
    const key = `${item.endpoint}|${item.url}|${item.status}|${item.elapsed_ms}`;
    if (seen[key]) return;
    seen[key] = true;
    combined.push(item);
  };
  lastSdkNetwork.forEach(append);
  (snapshot || []).forEach(append);
  return combined.slice(-12);
};

const missingAllowMeEndpoints = (network) =>
  EXPECTED_ALLOWME_ENDPOINTS.filter(
    (name) => !(network || []).some((item) => item && item.endpoint === name)
  );

const withSdkObservation = async (operation) => {
  const sdkConsole = [];
  const sdkNetwork = [];
  const startedAt = Date.now();
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalFetch =
    typeof window !== 'undefined' && typeof window.fetch === 'function'
      ? window.fetch
      : null;
  const xhrProto =
    typeof window !== 'undefined' &&
    window.XMLHttpRequest &&
    window.XMLHttpRequest.prototype
      ? window.XMLHttpRequest.prototype
      : null;
  const originalXhrOpen = xhrProto && xhrProto.open;
  const originalXhrSend = xhrProto && xhrProto.send;
  const originalXhrSetHeader = xhrProto && xhrProto.setRequestHeader;

  const pushConsole = (level, args) => {
    try {
      if (sdkConsole.length >= 12) return;
      const text = Array.prototype.map
        .call(args, stringifyLogArg)
        .join(' ')
        .slice(0, 400);
      if (text) sdkConsole.push(`${level}: ${text}`);
    } catch (error) {
      // fail-safe
    }
  };

  const pushNetwork = ({ url, method, status, elapsedMs, headers, error }) => {
    try {
      const endpoint = classifyAllowMeEndpoint(url);
      if (!endpoint || sdkNetwork.length >= 12) return;
      const numericStatus = typeof status === 'number' ? status : null;
      const entry = {
        endpoint,
        method: method ? String(method).toUpperCase() : undefined,
        url: sanitizeObservedUrl(url),
        status: numericStatus,
        ok: numericStatus != null ? numericStatus >= 200 && numericStatus < 400 : false,
        elapsed_ms: typeof elapsedMs === 'number' ? elapsedMs : undefined,
        request_headers: sanitizeHeaders(headers),
        error:
          error ||
          (numericStatus === 0 ? 'timeout_or_blocked' : undefined),
      };
      Object.keys(entry).forEach((key) => {
        if (entry[key] === undefined) delete entry[key];
      });
      sdkNetwork.push(entry);
      rememberSdkNetwork(entry);
      logStep('network', `${entry.endpoint} status=${entry.status}`, {
        method: entry.method,
        elapsed_ms: entry.elapsed_ms,
        ok: entry.ok,
        error: entry.error,
      });
    } catch (captureError) {
      // fail-safe
    }
  };

  console.error = function allowMeObservedError() {
    pushConsole('error', arguments);
    return originalError.apply(console, arguments);
  };
  console.warn = function allowMeObservedWarn() {
    pushConsole('warn', arguments);
    return originalWarn.apply(console, arguments);
  };

  if (originalFetch) {
    window.fetch = function allowMeObservedFetch(input, init) {
      const url = (input && input.url) || input;
      const method =
        (init && init.method) || (input && input.method) || 'GET';
      const headers = readFetchHeaders(input, init);
      const requestStartedAt = Date.now();
      return originalFetch.call(window, input, init).then(
        (response) => {
          pushNetwork({
            url,
            method,
            status: response && response.status,
            elapsedMs: Date.now() - requestStartedAt,
            headers,
          });
          return response;
        },
        (error) => {
          pushNetwork({
            url,
            method,
            status: 0,
            elapsedMs: Date.now() - requestStartedAt,
            headers,
            error: 'network',
          });
          throw error;
        }
      );
    };
  }

  if (xhrProto && originalXhrOpen && originalXhrSend) {
    xhrProto.open = function allowMeObservedOpen(method, url) {
      this.__amMethod = method;
      this.__amUrl = url;
      this.__amHeaders = {};
      this.__amStart = Date.now();
      try {
        return originalXhrOpen.apply(this, arguments);
      } catch (openError) {
        return undefined;
      }
    };
    if (originalXhrSetHeader) {
      xhrProto.setRequestHeader = function allowMeObservedSetHeader(name, value) {
        try {
          if (!this.__amHeaders) this.__amHeaders = {};
          this.__amHeaders[name] = value;
        } catch (headerError) {
          // fail-safe
        }
        try {
          return originalXhrSetHeader.apply(this, arguments);
        } catch (setHeaderError) {
          return undefined;
        }
      };
    }
    xhrProto.send = function allowMeObservedSend() {
      const xhr = this;
      try {
        xhr.addEventListener('loadend', () => {
          pushNetwork({
            url: xhr.__amUrl,
            method: xhr.__amMethod,
            status: typeof xhr.status === 'number' ? xhr.status : 0,
            elapsedMs: Date.now() - (xhr.__amStart || Date.now()),
            headers: xhr.__amHeaders,
          });
        });
      } catch (listenError) {
        // fail-safe
      }
      return originalXhrSend.apply(this, arguments);
    };
  }

  try {
    const result = await operation();
    return {
      result,
      sdkConsole,
      sdkNetwork,
      elapsedMs: Date.now() - startedAt,
    };
  } catch (error) {
    try {
      error.sdkConsole = sdkConsole;
      error.sdkNetwork = sdkNetwork;
      error.elapsedMs = Date.now() - startedAt;
    } catch (attachError) {
      // fail-safe
    }
    throw error;
  } finally {
    console.error = originalError;
    console.warn = originalWarn;
    if (originalFetch) {
      window.fetch = originalFetch;
    }
    if (xhrProto) {
      if (originalXhrOpen) xhrProto.open = originalXhrOpen;
      if (originalXhrSend) xhrProto.send = originalXhrSend;
      if (originalXhrSetHeader) xhrProto.setRequestHeader = originalXhrSetHeader;
    }
  }
};

const nextLoginCollectEvent = () => {
  collectAttempt += 1;
  const nonce = `${Date.now()}-${collectAttempt}`;
  return `${COLLECT_EVENT_PREFIX}:${nonce}`;
};

const fingerprintsAreEqual = (left, right) =>
  Boolean(left) &&
  Boolean(right) &&
  String(left).trim() === String(right).trim();

const invokeAlwmCollect = (collectEvent) =>
  window.Alwm.collect(collectEvent);

const firstHttpStatus = (sdkNetwork) => {
  if (!sdkNetwork || !sdkNetwork.length) return undefined;
  const hit = sdkNetwork.find(
    (item) => typeof item.status === 'number' && item.status >= 400
  );
  return hit ? hit.status : undefined;
};

const withInferredCause = (entry, inferArgs) => {
  const network = mergeSdkNetwork(inferArgs.sdkNetwork);
  const missingEndpoints = missingAllowMeEndpoints(network);
  const httpStatus = firstHttpStatus(network);
  const likelyCause = inferAllowMeLikelyCause({
    ...inferArgs,
    httpStatus,
  });
  return {
    ...entry,
    likelyCause,
    likelyCauseLabel: AllowMeLikelyCauseLabels[likelyCause],
    correctionHint: buildAllowMeCorrectionHint(likelyCause, inferArgs),
    apiKeyLength: inferArgs.apiKeyLength,
    sdkConsole:
      inferArgs.sdkConsole && inferArgs.sdkConsole.length
        ? inferArgs.sdkConsole
        : undefined,
    sdkNetwork: network.length ? network : undefined,
    missingEndpoints: missingEndpoints.length ? missingEndpoints : undefined,
    alwmMethods:
      inferArgs.alwmMethods && inferArgs.alwmMethods.length
        ? inferArgs.alwmMethods
        : undefined,
    elapsedMs: inferArgs.elapsedMs,
    initDurationMs: lastInitDurationMs,
    initResultType: lastInitResultType,
  };
};

export const resolveAllowMeConfig = (clientConfig) => {
  const allowMe =
    clientConfig && clientConfig.vars && clientConfig.vars.allowMe
      ? clientConfig.vars.allowMe
      : {};
  const isProduction = Number(process.env.REACT_APP_API_ENUM) === 0;
  const apiKey = isProduction ? allowMe.keyPrd : allowMe.keyHlg;
  const partnerEnabled = isProduction
    ? allowMe.enabledPrd !== false
    : allowMe.enabledHlg !== false;
  const ambiente = resolveAmbiente();
  const resolved = {
    apiKey: (apiKey || '').trim(),
    enabled: isAllowMeEnabled() && partnerEnabled,
  };

  logStep('config', 'Config resolvida', {
    ambiente,
    enabled: resolved.enabled,
    apiKey: maskApiKey(resolved.apiKey),
    allowMeEnvFlag:
      process.env.REACT_APP_ALLOWME_ENABLED === undefined
        ? '(unset)'
        : process.env.REACT_APP_ALLOWME_ENABLED,
  });
  logKeyDebug(resolved.apiKey, resolveSdkUrl(), ambiente);

  return resolved;
};

export const loadSdk = () => {
  if (typeof window === 'undefined') {
    const error = new Error('AllowMe SDK requires browser environment');
    logError('loadSdk', 'Ambiente sem window', error);
    return Promise.reject(error);
  }

  const sdkUrl = resolveSdkUrl();
  logKeyDebug('', sdkUrl, resolveAmbiente());

  if (window.Alwm) {
    logStep('loadSdk', 'SDK já disponível no window.Alwm', { sdkUrl });
    return Promise.resolve();
  }

  if (sdkLoadPromise) {
    logStep('loadSdk', 'Reutilizando promise de carregamento em andamento', {
      sdkUrl,
    });
    return sdkLoadPromise;
  }

  logStep('loadSdk', 'Carregando SDK', { sdkUrl });

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      logStep('loadSdk', 'Script já presente no DOM; aguardando evento load', {
        sdkUrl,
      });
      existingScript.addEventListener('load', () => {
        logStep('loadSdk', 'Script existente carregado com sucesso', { sdkUrl });
        resolve();
      });
      existingScript.addEventListener('error', () => {
        const error = new Error('AllowMe SDK script failed to load');
        logError('loadSdk', 'Falha ao carregar script existente', error, {
          sdkUrl,
        });
        reject(error);
      });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = sdkUrl;
    script.type = 'text/javascript';

    script.onload = () => {
      if (window.Alwm) {
        logStep('loadSdk', 'SDK carregada com sucesso', { sdkUrl });
        resolve();
        return;
      }
      const error = new Error('AllowMe SDK loaded but Alwm is unavailable');
      logError('loadSdk', 'Script carregou mas Alwm não está disponível', error, {
        sdkUrl,
      });
      reject(error);
    };

    script.onerror = () => {
      const error = new Error('AllowMe SDK script failed to load');
      logError('loadSdk', 'Erro de rede ao carregar script', error, { sdkUrl });
      reject(error);
    };

    document.head.appendChild(script);
  }).catch((error) => {
    sdkLoadPromise = null;
    throw error;
  });

  return sdkLoadPromise;
};

export const init = async ({ apiKey, enabled = isAllowMeEnabled() } = {}) => {
  initSucceeded = false;
  lastEnabled = enabled;
  lastInitDiagnostic = null;
  initPluginWasInvoked = false;
  lastInitDurationMs = null;
  lastInitResultType = null;
  lastSdkNetwork = [];
  const ambiente = resolveAmbiente();
  const normalizedKey = (apiKey || '').trim();
  const apiKeyPreview = maskApiKey(normalizedKey);

  if (!enabled) {
    logWarn('init', 'AllowMe desabilitada; init ignorado', {
      ambiente,
      allowMeEnvFlag:
        process.env.REACT_APP_ALLOWME_ENABLED === undefined
          ? '(unset)'
          : process.env.REACT_APP_ALLOWME_ENABLED,
    });
    return;
  }

  if (!normalizedKey) {
    lastInitDiagnostic = 'Init não chamou o SDK: API key vazia.';
    logWarn('init', 'API key vazia; init ignorado', { ambiente });
    void logAllowMeError(
      withInferredCause(
        allowMeInitMissingKeyEntry({
          apiKeyPreview,
          sdkUrl: resolveSdkUrl(),
          ambiente,
        }),
        { apiKeyLength: 0, kind: 'init_rejected', ambiente, sdkUrl: resolveSdkUrl() }
      )
    );
    return;
  }

  logStep('init', 'Iniciando init da SDK', {
    ambiente,
    apiKey: apiKeyPreview,
  });
  logKeyDebug(normalizedKey, resolveSdkUrl(), ambiente);

  if (initPromise) {
    logStep('init', 'Init já em andamento; aguardando conclusão');
    await initPromise;
    return;
  }

  initPromise = (async () => {
    try {
      const observed = await withSdkObservation(async () => {
        await loadSdk();

        if (!window.Alwm || typeof window.Alwm.init !== 'function') {
          const unavailable = new Error('Alwm.init indisponível após carregar SDK');
          unavailable.code = 'ALLOWME_INIT_UNAVAILABLE';
          throw unavailable;
        }

        logStep('init', 'Chamando Alwm.init');
        initPluginWasInvoked = true;
        return withTimeout(
          window.Alwm.init({ apiKey: normalizedKey }),
          INIT_TIMEOUT_MS,
          'Alwm.init'
        );
      });

      lastInitDurationMs = observed.elapsedMs;
      const initResult = observed.result;
      lastInitResultType =
        initResult == null ? String(initResult) : typeof initResult;

      if (isNativeFailureMessage(initResult)) {
        initSucceeded = false;
        lastInitDiagnostic = String(initResult).trim();
        logWarn('init', 'Alwm.init retornou erro de setup', {
          diagnostic: lastInitDiagnostic,
        });
        void logAllowMeError(
          withInferredCause(
            allowMeInitFailedEntry({
              message: lastInitDiagnostic,
              diagnostic: lastInitDiagnostic,
              apiKeyPreview,
              sdkUrl: resolveSdkUrl(),
              ambiente,
            }),
            {
              apiKeyLength: normalizedKey.length,
              kind: 'init_rejected',
              sdkConsole: observed.sdkConsole,
              sdkNetwork: observed.sdkNetwork,
              alwmMethods: listAlwmMethods(),
              elapsedMs: observed.elapsedMs,
              ambiente,
              sdkUrl: resolveSdkUrl(),
              httpStatus: firstHttpStatus(observed.sdkNetwork),
            }
          )
        );
        return;
      }
      initSucceeded = true;
      lastInitDiagnostic = initResult
        ? String(initResult)
        : 'allowme initialized';
      logStep('init', 'Alwm.init concluído com sucesso', {
        ambiente,
        apiKey: apiKeyPreview,
        initResult: lastInitDiagnostic,
        elapsedMs: observed.elapsedMs,
      });
    } catch (error) {
      initSucceeded = false;
      const isTimeout = error && error.code === 'ALLOWME_TIMEOUT';
      const isUnavailable = error && error.code === 'ALLOWME_INIT_UNAVAILABLE';
      const isLoadFailure =
        isUnavailable ||
        (error &&
          error.message &&
          /SDK script failed|Alwm is unavailable|browser environment/i.test(
            error.message
          ));
      lastInitDiagnostic = `Exceção no init: ${(error && error.message) || error}`;
      logError('init', 'Falha no init da SDK', error, {
        ambiente,
        apiKey: apiKeyPreview,
      });
      const kind = isLoadFailure
        ? 'load'
        : isTimeout
          ? 'init_timeout'
          : 'init_exception';
      void logAllowMeError(
        withInferredCause(
          allowMeInitExceptionEntry({
            error,
            apiKeyPreview,
            processStep: isLoadFailure
              ? AllowMeProcessSteps.sdkLoad
              : AllowMeProcessSteps.sdkInit,
            failedOperation: isLoadFailure ? 'loadSdk' : 'Alwm.init',
            sdkUrl: resolveSdkUrl(),
            ambiente,
            timeoutMs: isTimeout ? INIT_TIMEOUT_MS : undefined,
            hint: isTimeout
              ? 'Timeout no setup da SDK (Alwm.init), antes da coleta.'
              : isLoadFailure
                ? 'Falha ao carregar o script da AllowMe (CDN), antes do setup.'
                : 'Exceção durante o setup da SDK (Alwm.init).',
          }),
          {
            apiKeyLength: normalizedKey.length,
            kind,
            sdkConsole: error.sdkConsole,
            sdkNetwork: error.sdkNetwork,
            alwmMethods: listAlwmMethods(),
            elapsedMs: error.elapsedMs,
            ambiente,
            sdkUrl: resolveSdkUrl(),
            httpStatus: firstHttpStatus(error.sdkNetwork),
          }
        )
      );
    }
  })();

  try {
    await initPromise;
  } finally {
    initPromise = null;
  }
};

export const initAllowMeFromConfig = (clientConfig) => {
  const { apiKey, enabled } = resolveAllowMeConfig(clientConfig);
  logStep('boot', 'Disparando init assíncrono a partir da config do parceiro', {
    enabled,
    apiKey: maskApiKey(apiKey),
  });
  void init({ apiKey, enabled });
};

const collectDiagnostics = () => ({
  allowMeFlag: lastEnabled,
  allowMeCanCollect: initSucceeded,
  allowMeInitPluginInvoked: initPluginWasInvoked,
  allowMeLastInitDiagnostic: lastInitDiagnostic,
  sdkUrl: resolveSdkUrl(),
  ambiente: resolveAmbiente(),
});

const recordCollectFailure = (entry) => {
  lastCollectFailure = {
    causeReason: entry.causeReason,
    message: entry.message,
    processStep: entry.processStep,
    timeoutMs: entry.timeoutMs,
    failedOperation: entry.failedOperation,
    likelyCause: entry.likelyCause,
    likelyCauseLabel: entry.likelyCauseLabel,
    correctionHint: entry.correctionHint,
    apiKeyLength: entry.apiKeyLength,
    sdkConsole: entry.sdkConsole,
    sdkNetwork: entry.sdkNetwork,
    missingEndpoints: entry.missingEndpoints,
    elapsedMs: entry.elapsedMs,
  };
  void logAllowMeError(entry);
};

export const collectLoginFingerprint = async (clientConfig) => {
  logStep('collect', 'Iniciando collect do fingerprint');
  lastCollectFailure = null;
  let apiKey;
  let usedCollectEvent;

  try {
    const resolved = resolveAllowMeConfig(clientConfig);
    apiKey = resolved.apiKey;
    const { enabled } = resolved;
    if (!enabled) {
      logWarn('collect', 'AllowMe desabilitada; retornando null');
      return null;
    }

    if (!initSucceeded) {
      logWarn('collect', 'Init não concluído; pulando collect');
      const inferred = withInferredCause(
        {},
        {
          apiKeyLength: (apiKey || '').length,
          kind: 'init_not_succeeded',
          ambiente: resolveAmbiente(),
          sdkUrl: resolveSdkUrl(),
        }
      );
      lastCollectFailure = {
        causeReason: AllowMeErrorCauseReasons.initNotSucceeded,
        message: lastInitDiagnostic
          ? `AllowMe init did not succeed (${lastInitDiagnostic}); fingerprint not sent`
          : 'AllowMe init did not succeed; fingerprint not sent',
        processStep: AllowMeProcessSteps.sdkInit,
        failedOperation: 'Alwm.init',
        ...inferred,
      };
      return null;
    }

    if (!window.Alwm || typeof window.Alwm.collect !== 'function') {
      logWarn('collect', 'Alwm.collect indisponível; retornando null', {
        hasAlwm: Boolean(window.Alwm),
      });
      recordCollectFailure(
        withInferredCause(
          buildAllowMeErrorEntry({
            reason: AllowMeErrorReasons.collectFailed,
            phase: AllowMeErrorPhases.loginCollect,
            outcome: AllowMeErrorOutcomes.loginContinuedWithoutFingerprint,
            causeReason: AllowMeErrorCauseReasons.collectFailed,
            message: 'Alwm.collect indisponível após o setup',
            processStep: AllowMeProcessSteps.sdkCollect,
            failedOperation: 'Alwm.collect',
            ...collectDiagnostics(),
            allowMeApiKeyPreview: maskApiKey(apiKey),
            alwmMethods: listAlwmMethods(),
          }),
          {
            apiKeyLength: (apiKey || '').length,
            kind: 'missing_api',
            alwmMethods: listAlwmMethods(),
            ambiente: resolveAmbiente(),
            sdkUrl: resolveSdkUrl(),
          }
        )
      );
      return null;
    }

    const collectEvent = nextLoginCollectEvent();
    logStep('collect', 'Chamando Alwm.collect (tentativa nova)', {
      collectEvent,
      attempt: collectAttempt,
    });
    let observed = await withSdkObservation(() =>
      withTimeout(
        invokeAlwmCollect(collectEvent),
        COLLECT_TIMEOUT_MS,
        `Alwm.collect('${collectEvent}')`
      )
    );
    let fingerprint = observed.result;
    usedCollectEvent = collectEvent;

    if (
      isValidFingerprint(fingerprint) &&
      fingerprintsAreEqual(fingerprint, lastCollectedFingerprint)
    ) {
      const retryEvent = nextLoginCollectEvent();
      logWarn(
        'collect',
        'SDK devolveu o mesmo fingerprint da tentativa anterior; coletando de novo',
        {
          previousPreview: maskFingerprint(lastCollectedFingerprint),
          retryEvent,
        }
      );
      observed = await withSdkObservation(() =>
        withTimeout(
          invokeAlwmCollect(retryEvent),
          COLLECT_TIMEOUT_MS,
          `Alwm.collect('${retryEvent}')`
        )
      );
      fingerprint = observed.result;
      usedCollectEvent = retryEvent;
    }

    if (!isValidFingerprint(fingerprint)) {
      const preview = maskFingerprint(fingerprint);
      const isNativeError = isNativeFailureMessage(fingerprint);
      logWarn('collect', 'Fingerprint inválido ou vazio; retornando null', {
        preview,
      });
      recordCollectFailure(
        withInferredCause(
          buildAllowMeErrorEntry({
            reason: isNativeError
              ? AllowMeErrorReasons.collectFailed
              : AllowMeErrorReasons.collectEmpty,
            phase: AllowMeErrorPhases.loginCollect,
            outcome: AllowMeErrorOutcomes.loginContinuedWithoutFingerprint,
            causeReason: isNativeError
              ? AllowMeErrorCauseReasons.collectFailed
              : AllowMeErrorCauseReasons.collectEmpty,
            message: isNativeError
              ? String(fingerprint).trim()
              : 'AllowMe collect returned null/empty',
            processStep: AllowMeProcessSteps.sdkCollect,
            failedOperation: "Alwm.collect('login')",
            collectEvent: usedCollectEvent,
            hint: isNativeError
              ? 'Falha retornada pelo SDK no collect (não no Alwm.init).'
              : undefined,
            ...collectDiagnostics(),
            allowMeApiKeyPreview: maskApiKey(apiKey),
          }),
          {
            apiKeyLength: (apiKey || '').length,
            kind: isNativeError ? 'native_error' : 'empty',
            sdkConsole: observed.sdkConsole,
            sdkNetwork: observed.sdkNetwork,
            alwmMethods: listAlwmMethods(),
            elapsedMs: observed.elapsedMs,
            ambiente: resolveAmbiente(),
            sdkUrl: resolveSdkUrl(),
            httpStatus: firstHttpStatus(observed.sdkNetwork),
          }
        )
      );
      return null;
    }

    const trimmed = fingerprint.trim();
    const sameAsPreviousAttempt = fingerprintsAreEqual(
      trimmed,
      lastCollectedFingerprint
    );
    lastCollectedFingerprint = trimmed;
    lastSdkNetwork = [];
    logStep('collect', 'Fingerprint gerado com sucesso', {
      preview: maskFingerprint(trimmed),
      elapsedMs: observed.elapsedMs,
      collectEvent: usedCollectEvent,
      attempt: collectAttempt,
      sameAsPreviousAttempt,
    });
    if (sameAsPreviousAttempt) {
      logWarn(
        'collect',
        'Fingerprint ainda idêntico após retry; enviando mesmo assim'
      );
    }
    return trimmed;
  } catch (error) {
    const isTimeout = error && error.code === 'ALLOWME_TIMEOUT';
    logError('collect', 'Erro ao coletar fingerprint; retornando null', error);
    recordCollectFailure(
      withInferredCause(
        buildAllowMeErrorEntry({
          reason: AllowMeErrorReasons.collectException,
          phase: AllowMeErrorPhases.loginCollect,
          outcome: AllowMeErrorOutcomes.loginContinuedWithoutFingerprint,
          error,
          causeReason: isTimeout
            ? AllowMeErrorCauseReasons.timeout
            : AllowMeErrorCauseReasons.exception,
          message: isTimeout
            ? `Timeout de ${COLLECT_TIMEOUT_MS}ms na coleta do fingerprint (Alwm.collect), após o setup (Alwm.init) ter concluído.`
            : (error && error.message) || 'Erro ao coletar fingerprint',
          processStep: AllowMeProcessSteps.sdkCollect,
          failedOperation: "Alwm.collect('login')",
          collectEvent: usedCollectEvent,
          timeoutMs: isTimeout ? COLLECT_TIMEOUT_MS : undefined,
          hint: isTimeout
            ? 'O setup (Alwm.init) não falhou. A chave inválida na web costuma estourar aqui, no collect, e não no init.'
            : undefined,
          ...collectDiagnostics(),
          allowMeApiKeyPreview: maskApiKey(apiKey),
        }),
        {
          apiKeyLength: (apiKey || '').length,
          kind: isTimeout ? 'timeout' : 'exception',
          sdkConsole: error.sdkConsole,
          sdkNetwork: error.sdkNetwork,
          alwmMethods: listAlwmMethods(),
          elapsedMs: error.elapsedMs,
          ambiente: resolveAmbiente(),
          sdkUrl: resolveSdkUrl(),
          httpStatus: firstHttpStatus(error.sdkNetwork),
        }
      )
    );
    return null;
  }
};

export const getCanCollect = () => initSucceeded;

export const getLastInitDiagnostic = () => lastInitDiagnostic;

export const getInitPluginWasInvoked = () => initPluginWasInvoked;

export const getLastCollectFailure = () => lastCollectFailure;

export const logFingerprintNotSent = ({
  document,
  causeReason,
  message,
  loginFlow = 'password',
} = {}) => {
  const failure = lastCollectFailure || {};
  void logAllowMeError(
    buildAllowMeErrorEntry({
      reason: AllowMeErrorReasons.fingerprintNotSent,
      phase: AllowMeErrorPhases.login,
      outcome: AllowMeErrorOutcomes.loginContinuedWithoutFingerprint,
      causeReason: causeReason || failure.causeReason,
      message: message || failure.message || 'Fingerprint not sent',
      document,
      loginFlow,
      processStep: AllowMeProcessSteps.loginSend,
      originProcessStep: failure.processStep,
      failedOperation: failure.failedOperation || 'termica/login',
      timeoutMs: failure.timeoutMs,
      hint: failure.correctionHint ||
        (failure.processStep ? `Falha original em ${failure.processStep}` : undefined),
      likelyCause: failure.likelyCause,
      likelyCauseLabel: failure.likelyCauseLabel,
      correctionHint: failure.correctionHint,
      apiKeyLength: failure.apiKeyLength,
      sdkConsole: failure.sdkConsole,
      sdkNetwork: failure.sdkNetwork,
      missingEndpoints: failure.missingEndpoints,
      elapsedMs: failure.elapsedMs,
      ...collectDiagnostics(),
    })
  );
};

export const resetAllowMeStateForTests = () => {
  initSucceeded = false;
  sdkLoadPromise = null;
  initPromise = null;
  lastEnabled = true;
  lastInitDiagnostic = null;
  initPluginWasInvoked = false;
  lastCollectFailure = null;
  lastInitDurationMs = null;
  lastInitResultType = null;
  collectAttempt = 0;
  lastCollectedFingerprint = null;
  lastSdkNetwork = [];

  if (typeof document !== 'undefined') {
    const script = document.getElementById(SCRIPT_ID);
    if (script) {
      script.remove();
    }
  }

  if (typeof window !== 'undefined') {
    delete window.Alwm;
  }
};

export const getLastEnabledForTests = () => lastEnabled;
